import * as React from 'react';
import { resolve } from 'inversify-react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';

import { ConfigurationService } from 'src/core/services/configuration.service';
import { ContextService } from 'src/core/services/context.service';
import { DialogService } from 'src/core/services/ui/dialog.service';
import { ToastNotifications } from 'src/core/ui/toast';
import { ObjectUtils } from 'src/core/utils/object';
import { ControllerProps } from 'src/router';
import { DocumentTemplateCategoryListView } from './document-template-category-list.view';
import { DocumentTemplateCategoryListDataService } from './document-template-category-list.data';

interface State {
	defaultLanguage: string;
	mode: ControlMode;
}

@observer
export class DocumentTemplateCategoryListController extends React.Component<ControllerProps<any>, State> {
	@resolve
	configurationService: ConfigurationService;

	@resolve
	contextService: ContextService;

	@resolve
	dialogService: DialogService;

	@resolve
	toast: ToastNotifications;

	@observable
	dataService: DocumentTemplateCategoryListDataService;

	constructor(props: ControllerProps<any>) {
		super(props);

		this.state = {
			defaultLanguage: '',
			mode: 'display'
		};
	}

	componentDidMount() {
		this.props.setBusy(true);

		Promise.all([this.loadConfiguration(), this.loadData()])
			.catch((error) => {
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			})
			.then(() => {
				this.props.setBusy(false);
			});
	}

	private async loadConfiguration(): Promise<void> {
		const appConfig = await this.configurationService.current();
		this.setState({
			defaultLanguage: appConfig.defaultLanguage
		});
	}

	private async loadData(): Promise<void> {
		this.dataService = DocumentTemplateCategoryListDataService.make(this.contextService);

		this.dataService.addSorting('id', 'asc');
		this.dataService.pageSize = 25;

		await this.dataService.load(query => query);
	}

	handleModeChange = (mode: ControlMode): void => {
		this.setState({ mode: mode });
	};

	isValidItem = (item: DocumentTemplateCategoryItem): boolean => {
		const { defaultLanguage } = this.state;
		const errors = [];
		const defaultDisplayName = item.dynamicProperties.displayName[defaultLanguage];
		if (defaultDisplayName == null || defaultDisplayName.trim() == '') {
			errors.push('Name is required');
		} else if (item.type == null) {
			errors.push('Type is required');
		}

		const others = this.dataService.items.filter(m => m != item);
		const names = { ...item.dynamicProperties['displayName'] };
		delete names['__isProxy']; // just in case the object is a proxy
		delete names['@odata.type']; // when the object come from the server
		for (let language of Object.getOwnPropertyNames(names)) {
			if (others.some((m) => m.dynamicProperties['displayName'][language] === item.dynamicProperties['displayName'][language])) {
				errors.push(`There are two children with the same name '${language}: [${names[language]}]'`);
				break;
			}
		}

		if (errors.length > 0) {
			this.toast.showToast('Validation error!', errors, 'error');
			return false;
		}

		return true;
	};

	createNewItem = async (): Promise<DocumentTemplateCategoryItem> => {
		const { defaultLanguage } = this.state;
		const item = await this.dataService.add(defaultLanguage);

		return item;
	};

	save = async (): Promise<void> => {
		if (!this.dataService.items.every(this.isValidItem)) {
			return;
		}

		this.props.setBusy(true);

		try {
			await this.dataService.save();
			this.toast.showDataSaved();
		} catch (error) {
			this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
		}

		this.props.setBusy(false);
	};

	cancel = async (): Promise<void> => {
		this.props.setBusy(true);

		if (this.dataService.isDirty) {
			const dialogResult = await this.dialogService.showYesNo('Warning', 'Are you sure want to discard the changes? Your changes will be lost!');
			if (dialogResult) {
				await this.dataService.reload();
			}
		}

		this.handleModeChange('display');
		this.props.setBusy(false);
	};

	render() {
		if (this.dataService == null || this.dataService.items == null) {
			return null;
		}

		const { defaultLanguage, mode } = this.state;

		return (
			<DocumentTemplateCategoryListView
				model={this.dataService}
				defaultLanguage={defaultLanguage}
				mode={mode}
				onModeChange={this.handleModeChange}
				isValidItem={this.isValidItem}
				createNewItem={this.createNewItem}
				save={this.save}
				cancel={this.cancel}
				setBusy={this.props.setBusy}
			/>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/general/document-template-categories/list/document-template-category-list.controller.tsx